<!--
  Copyright (C) 2020 ExploRêve

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.

  Full source code is available at <https://gitlab.com/exploreve/frontend>.
-->

<template>
  <div class="immersion03">
    <NavBar />

    <div v-if="isWrongBrowser()">
      <h1>Bienvenue !</h1>
      <p>
        Oups, il semblerait que tu utilises un navigateur sur lequel notre site a du mal à fonctionner...
        <br />Pour jouer à ExploRêve, et plus généralement pour soutenir un développement libre d'internet, <br />nous
        t'invitons fortement à télécharger et utiliser Firefox, en allant
        <a href="https://www.mozilla.org/fr/firefox/new/">ici</a>
      </p>
      <img src="https://cdn.exploreve.fr/content/personnages/leon.svg" alt="leon" />
      <img src="https://cdn.exploreve.fr/content/personnages/daria.svg" alt="daria" />
      <img src="https://cdn.exploreve.fr/content/personnages/erwin.svg" alt="erwin" />
    </div>
    <div v-else>
      <div v-if="isMobile()">
        <h1>Bienvenue !</h1>
        <p>
          Pour entrer dans cette 3ème immersion, un ordinateur est nécessaire. En attendant, n'hésite pas nous rejoindre
          sur les réseaux sociaux, à parcourir le blog et à lire la page à propos pour en savoir plus sur le projet !
        </p>
        <img src="https://cdn.exploreve.fr/content/personnages/daria.svg" alt="daria" />
      </div>
      <router-view v-else @audio="setAudio" @talkie="playTalkie" />
    </div>
    <audio id="talkie_dialogue1" src="https://cdn.exploreve.fr/content/IM3/musiques/Dialogues_1.mp3" />
    <audio id="talkie_dialogue2" src="https://cdn.exploreve.fr/content/IM3/musiques/Dialogues_2.mp3" />
    <audio id="talkie_dialogue3" src="https://cdn.exploreve.fr/content/IM3/musiques/Dialogues_3.mp3" />
    <audio id="talkie_dariaerwin" src="https://cdn.exploreve.fr/content/IM3/musiques/Daria_et_Erwin.mp3" />

    <audio :src="musics[i - 1]" v-for="i of musics.length" :key="i" :id="'music0' + (i - 1)"></audio>
    <SoundVolumeButton class="volume-button" @audio="setAudio" />
  </div>
</template>

<script>
// @ is an alias to /src
import NavBar from "@/components/NavBar.vue";
import SoundVolumeButton from "@/components/SoundVolumeButton.vue";

export default {
  name: "immersion03",
  components: {
    NavBar,
    SoundVolumeButton
  },
  data: function () {
    return {
      volume: 0.6,
      muted: false,
      musics: [
        "https://cdn.exploreve.fr/content/IM3/musiques/Ambiance_Voyage_Ballon.mp3",
        "https://cdn.exploreve.fr/content/IM3/musiques/Pigeon_et_lettre.mp3",
        "https://cdn.exploreve.fr/content/IM3/musiques/pendant_echange_messages.mp3",
        "https://cdn.exploreve.fr/content/IM3/musiques/pages_intro_explic.mp3",
        "https://cdn.exploreve.fr/content/IM3/musiques/lecture_p_carte.mp3",
        "https://cdn.exploreve.fr/content/IM3/musiques/page_conclu_cabane.mp3"
      ],
      currentMusic: null
    };
  },
  mounted: function () {},
  methods: {
    isMobile: function () {
      return document.documentElement.clientWidth < 760;
    },

    isWrongBrowser: function () {
      const ua = window.navigator.userAgent;
      return ua.indexOf("Edge") !== -1 || ua.indexOf("Edg") !== -1 || ua.indexOf("MSIE") !== -1;
    },

    pauseMusic: function (musicNode) {
      if (this.isMusicNode(musicNode)) {
        musicNode.pause();
      }
    },

    playMusic: function (musicNode, loop) {
      if (this.isMusicNode(musicNode)) {
        musicNode.loop = loop || false;
        musicNode.muted = this.muted;
        musicNode.volume = this.volume;
        musicNode.play().catch(() => {});
      }
    },

    /**
     * send the "talkie" event with the event object:
     * {
     *   "talkie_id": <id of the talkie to run> (check the audio elements in this file),
     *   "on_end": function to call when talkie is done playing.
     * }
     * So, in a component, just
     * ```
     * this.$emit("talkie", {talkie_id: "talkie01", on_end: () => {}});
     * ```
     * If you need this event to bubble up from a subcomponent, call it with
     * ```
     * <MyComponent @talkie="reemitTalkie" />
     * ```
     * and implement emitTalkie method in your current component:
     * ```
     * reemitTalkie(event) {
     *   this.$emit("talkie", event);
     * }
     * ```
     */

    playTalkie: function (data) {
      const talkie = document.getElementById(data.talkie_id);
      if (this.isMusicNode(talkie)) {
        this.pauseMusic(this.currentMusic);
        talkie.muted = false;
        talkie.volume = 1;
        const onEnd = () => {
          talkie.removeEventListener("ended", onEnd);
          this.playMusic(this.currentMusic);
          data.on_end(data.talkie_id);
        };
        talkie.addEventListener("ended", onEnd);
        talkie.play().catch(onEnd);
      }
    },

    setAudio: function (music) {
      switch (music.action) {
        case "play": {
          this.pauseMusic(this.currentMusic);

          this.currentMusic = document.getElementById(music.id);
          this.playMusic(this.currentMusic, true);

          break;
        }
        case "play-once": {
          this.pauseMusic(this.currentMusic);

          let tmp = document.getElementById(music.id);
          this.playMusic(tmp, false);

          if (tmp != null && typeof tmp.addEventListener === "function") {
            tmp.addEventListener("ended", () => {
              this.playMusic(this.currentMusic, true);
            });
          }
          break;
        }
        case "pause":
          this.pauseMusic(this.currentMusic);
          break;
        case "mute":
          this.muted = music.muted;
          if (this.isMusicNode(this.currentMusic)) {
            this.currentMusic.muted = music.muted;
          }
          break;
        case "volume":
          this.volume = music.volume;
          if (this.isMusicNode(this.currentMusic)) {
            this.currentMusic.volume = music.volume;
          }
          break;
      }
    },

    isMusicNode: function (mus) {
      return mus != null && mus != undefined && typeof mus.play === "function";
    }
  }
};
</script>

<style lang="scss" scoped>
.immersion03 {
  p {
    padding: 20px;
  }
  img {
    height: 300px;
    margin: auto;
    margin-bottom: 30px;
  }
  .volume-button {
    position: fixed;
    bottom: 50px;
    right: 0;
    margin: 30px;
    z-index: 500;
  }
}
</style>
